import React, { useEffect, useRef, useState } from 'react'
import { withPrefix } from 'gatsby'
import { object } from 'prop-types'
import { Helmet } from 'react-helmet'
import { Script } from 'gatsby'

import { linkResolver } from '../../util/linkResolver'
import { formatDuration } from '../../util/timeDateUtils'
import { videoIdRegex } from '../../util/video'
import TranscriptionLink from './TranscriptionLink'
import * as Styled from './styles/VideoBlock.styles'

const BitmovinVideo = ({ streamId, nextVideoLink }) => {
  const timer = useRef(null)
  const nextVideoUrl = nextVideoLink ? linkResolver(nextVideoLink) : null

  const handleEnded = () => {
    if (nextVideoLink) {
      timer.current = setTimeout(() => {
        window.location.href = nextVideoUrl
      }, 2000)
    }
  }

  return (
    <>
      <Script
        type="module"
        src="https://streams.bitmovin.com/js/component.js"
        onLoad={() => {
          const bitmovinStream = document.createElement('bitmovin-stream')
          bitmovinStream.setAttribute('stream-id', streamId)
          bitmovinStream.setAttribute('autoplay', true)
          const playerConfig = {
            key: process.env.GATSBY_BITMOVIN_LICENSE_KEY,
            // playback: {
            //   muted: true,
            //   autoplay: false
            // },
            location: {
              google_ima:
                'https://imasdk.googleapis.com/js/sdkloader/ima3_debug.js',
            },
          }
          bitmovinStream.setAttribute('config', JSON.stringify(playerConfig))

          bitmovinStream.addEventListener('ended', () => {
            handleEnded()
          })

          document
            .getElementById('bitmovin-container')
            .appendChild(bitmovinStream)
        }}
      />
      <div id="bitmovin-container"></div>
    </>
  )
}

const YoutubeVideo = ({ videoUrl, videoTitle }) => (
  <>
    <Helmet>
      <script
        src="https://www.youtube.com/iframe_api"
        async
        defer
        type="text/javascript"
      />
    </Helmet>
    <iframe
      src={`https://youtube.com/embed/${videoUrl}?enablejsapi=1`}
      title={videoTitle}
      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
      frameBorder="0"
      webkitallowfullscreen="true"
      mozallowfullscreen="true"
      allowFullScreen
      autoPlay={true}
    />
  </>
)

const VideoBlock = ({ data, bottomPaddingAdjustment }) => {
  const isBrowser =
    typeof window !== 'undefined' && typeof document !== 'undefined'
  const currentPageUrl = isBrowser ? window.location.href : ''
  const currentPageTitle = isBrowser ? document.title : ''

  const {
    bitmovin_stream_id,
    bottom_padding_adjustment,
    caption,
    npfLogo,
    schema_publish_date,
    schema_video_description,
    transcription_link,
    video_duration_hours,
    video_duration_minutes,
    video_duration_seconds,
    video_link,
    video_thumbnail,
    video_title,
    video_width,
  } = data

  const videoDuration = formatDuration(
    video_duration_hours,
    video_duration_minutes,
    video_duration_seconds
  )

  const isBitmovinVideo = !!bitmovin_stream_id
  const uploadDate = schema_publish_date
    ? `${schema_publish_date.replace(/-/g, '')}T12:00:00Z`
    : ''
  return (
    <>
      <Helmet>
        {/* NOTE: This import is causing errors. Disabling 6/9/23, investigate further */}
        {/* <script src={withPrefix('amp-ga.min.js')} type="text/javascript" /> */}
        <script type="application/ld+json">
          {`
          {
            "@context": "http://schema.org",
            "@type": "VideoObject",
            "description": "${schema_video_description ?? ''}",
            "duration": "${videoDuration ?? ''}",
            "uploadDate" : "${uploadDate}",
            "name": "${video_title?.text ?? ''}",
            "publisher" : {
              "@type": "Organization",
              "name" : "National Psoriasis Foundation",
              "logo" : {
                "@type": "ImageObject",
                "url" : "${npfLogo ?? ''}"
              }
            },
            "thumbnailUrl": "${video_thumbnail?.url ?? ''}",
            "isPartOf": {
              "@type": "WebPage",
              "@id": "${currentPageUrl}",
              "name": "${video_title?.text ?? currentPageTitle ?? ''}"
            }
          }
        `}
        </script>
      </Helmet>
      <Styled.VideoBlock
        as="figure"
        bottomPaddingAdjustment={bottomPaddingAdjustment}
      >
        <Styled.VideoContainer widthAdjustment={video_width}>
          <Styled.IFrameContainer>
            {isBitmovinVideo && (
              <BitmovinVideo
                streamId={bitmovin_stream_id}
                // Note, the confusing prop name `azure_next_video_link` is a result of us not being able to change the exisiting Prismic field's API id.
                nextVideoLink={null}
              />
            )}
            {!isBitmovinVideo && (
              <YoutubeVideo
                videoUrl={
                  video_link?.embed_url && videoIdRegex(video_link.embed_url)
                }
                videoTitle={video_title?.text ?? ''}
              />
            )}
          </Styled.IFrameContainer>
        </Styled.VideoContainer>
        <Styled.VideoCaption>
          {caption?.text ?? ''}
          {transcription_link && (
            <TranscriptionLink link={transcription_link} />
          )}
        </Styled.VideoCaption>
      </Styled.VideoBlock>
    </>
  )
}

VideoBlock.propTypes = {
  data: object,
}

export default VideoBlock
